import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter email and password");
      return;
    }
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden flex items-center justify-center px-6 noise" data-testid="admin-login-page">
      <Seo title="Admin Login" path="/admin/login" description="Spin Labs admin sign-in." noindex />
      <div className="absolute inset-0 grid-bg-lg opacity-40" />
      <div className="spotlight w-[500px] h-[500px] bg-[#00F0FF]/20 top-0 left-0" />
      <div className="spotlight w-[500px] h-[500px] bg-[#FF00FF]/20 bottom-0 right-0" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF00FF] flex items-center justify-center">
            <Zap className="w-5 h-5 text-black" strokeWidth={3} />
          </div>
          <div className="font-display font-black text-xl">SPIN<span className="text-[#00F0FF]">.</span>LABS</div>
        </Link>

        <div className="glass rounded-2xl p-8 border border-white/10">
          <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-3">Restricted Area</div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Admin Access</h1>
          <p className="mt-2 text-sm text-white/60">Sign in to review incoming project briefs.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="al-email" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Email</Label>
              <Input
                id="al-email"
                data-testid="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@labs.in"
                className="mt-2 h-12 bg-black/40 border-white/10 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                required
              />
            </div>
            <div>
              <Label htmlFor="al-password" className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">Password</Label>
              <Input
                id="al-password"
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 bg-black/40 border-white/10 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="btn-primary !w-full !py-4 !text-base disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (<>Sign in <ArrowRight className="w-5 h-5" /></>)}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-white/40 hover:text-white font-mono-neo uppercase tracking-[0.2em]">
            ← Back to site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
